import { useState } from "react";
import produce from "immer";
import { useTranslation } from "react-i18next";
import { Box, ListItem, ListItemText } from "@material-ui/core";
import ButtonWithLoading from "components/ButtonWithLoading";
import { StyledChipField } from "components/Forms/StyledComponents";
import useOrganization from "utils/useOrganization";
import { isValidHostname, isValidIpv4OrCidr } from "utils/string";

import { ResponseOrganization } from "@dashboard-v3/api";

type Form = {
  hostnames: { values: string[]; error: string };
  ipMasks: { values: string[]; error: string };
};

export default function IgnoreTracking() {
  const { t } = useTranslation("storage");
  const { isLoading, organization, updateOrganization } = useOrganization();
  const [isSaving, setIsSaving] = useState(false);
  const [form, setForm] = useState<Form>(initForm(organization));

  function handleChange(action: "ADD" | "REMOVE", field: keyof Form) {
    return (value: string) => {
      setForm(prev =>
        produce(prev, draft => {
          const savedValues = draft[field].values;
          draft[field].error = "";

          if (action === "REMOVE") {
            draft[field].values = savedValues.filter(v => v !== value);
            return;
          }

          if (field === "ipMasks" && !isValidIpv4OrCidr(value)) {
            draft[field].error = "ignoreTracking.ipMask.invalid";
          }
          if (field === "hostnames" && !isValidHostname(value)) {
            draft[field].error = "ignoreTracking.hostname.invalid";
          }

          if (!draft[field].error) {
            draft[field].values = [...savedValues, value];
          }
        })
      );
    };
  }

  async function handleSave() {
    const { ipMasks, hostnames } = form;
    setIsSaving(true);
    const storagesPolicy = {
      ignoreTrackingIpMasks: ipMasks.values,
      ignoreTrackingHosts: hostnames.values,
    };
    await updateOrganization({ storagesPolicy });
    setIsSaving(false);
  }

  const isSaveDisabled = () => {
    const { storagesPolicy } = organization;
    const { ipMasks, hostnames } = form;
    if (isLoading) return true;

    return (
      !storagesPolicy.ignoreTrackingIpMasks?.length &&
      !storagesPolicy.ignoreTrackingHosts?.length &&
      !hostnames.values.length &&
      !ipMasks.values.length
    );
  };

  return (
    <ListItem
      style={{ flexDirection: "column", alignItems: "stretch", gap: 10 }}
    >
      <ListItemText
        primary={t("ignoreTracking.title")}
        secondary={t("ignoreTracking.subtitle")}
        primaryTypographyProps={{ variant: "subtitle2" }}
        secondaryTypographyProps={{ variant: "caption" }}
      />
      <StyledChipField
        variant="outlined"
        fullWidth
        label={t("ignoreTracking.ipMask.label")}
        value={form.ipMasks.values}
        onAdd={handleChange("ADD", "ipMasks")}
        onDelete={handleChange("REMOVE", "ipMasks")}
        allowDuplicates={false}
        disabled={isLoading || isSaving}
        error={Boolean(form.ipMasks.error)}
        helperText={t(form.ipMasks.error)}
      />
      <StyledChipField
        variant="outlined"
        fullWidth
        label={t("ignoreTracking.hostnames.label")}
        value={form.hostnames.values}
        onAdd={handleChange("ADD", "hostnames")}
        onDelete={handleChange("REMOVE", "hostnames")}
        allowDuplicates={false}
        disabled={isLoading || isSaving}
        error={Boolean(form.hostnames.error)}
        helperText={t(form.hostnames.error)}
      />
      <Box marginTop="20px" textAlign="left" width="100%">
        <ButtonWithLoading
          variant="contained"
          size="small"
          color="primary"
          disableElevation
          loading={isLoading || isSaving}
          disabled={isSaveDisabled()}
          onClick={handleSave}
        >
          {t("common:save")}
        </ButtonWithLoading>
      </Box>
    </ListItem>
  );
}

function initForm(organization: ResponseOrganization) {
  const storagesPolicy = organization.storagesPolicy;

  return (): Form => ({
    ipMasks: { error: "", values: storagesPolicy?.ignoreTrackingIpMasks || [] },
    hostnames: { error: "", values: storagesPolicy?.ignoreTrackingHosts || [] },
  });
}
